import { OutputTechnology, RoleName } from '@enmon/commons/lib/tools';
import { Col, Flex, Input, Select } from 'antd';
import { useCallback, type VFC } from 'react';
import { FilterControlButtons } from 'components/form/FilterControlButtons';
import { Button, Filter, FilterDivider, Form } from 'components';
import { Icon } from 'components/icon/Icon';
import { ManufacturersSelect } from 'components/form/select/ManufacturersSelect';
import { outputTechnologyTranslated } from 'routes/portfolio/locations/const';
import type { Paths } from 'type-fest';
import { CommoditiesSelect } from '@/components/form/select/CommoditiesSelect';
import { useHistory } from 'react-router-dom';
import {
  DEFAULT_FILTER,
  type ModelsFilter,
  useModelsListFilterWithPagination,
} from '../hooks/useModelsListFilterWithPagination';
import { useUserInfo } from '../../../../../../../hooks/useUser';

export const ModelListFilter: VFC = () => {
  const history = useHistory();
  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;

  const [form] = Form.useForm<ModelsFilter>();
  const selectedCommodity = Form.useWatch('commodity' satisfies Paths<ModelsFilter>, form);

  const { filter, setFilterState, resetFilterState, setPaginationState } = useModelsListFilterWithPagination();

  const handleCreate = useCallback(() => {
    history.push(`${history.location.pathname}/create`);
  }, [history]);

  const handleReset = () => {
    form.setFieldsValue(DEFAULT_FILTER);

    resetFilterState();
    setPaginationState({
      current: 1,
    });
  };

  const handleFinish = (values: ModelsFilter) => {
    setFilterState(values);
    setPaginationState({
      current: 1,
    });
  };

  return (
    <Filter form={form} initialValues={filter} onFinish={handleFinish}>
      <Form.Item label="Search" name={'search' satisfies Paths<ModelsFilter>}>
        <Input allowClear placeholder="Model name..." />
      </Form.Item>

      <Form.Item label="Commodity" name={'commodity' satisfies Paths<ModelsFilter>}>
        <CommoditiesSelect />
      </Form.Item>

      <Form.Item label="Manufacturer" name={'manufacturerId' satisfies Paths<ModelsFilter>}>
        <ManufacturersSelect
          allowClear
          maxTagCount="responsive"
          maxTagTextLength={20}
          commodity={selectedCommodity}
          placeholder="Select manufacturer"
        />
      </Form.Item>

      <Form.Item label="Technologies" name={'technologies' satisfies Paths<ModelsFilter>}>
        <Select
          allowClear
          maxTagTextLength={20}
          mode="multiple"
          options={Object.values(OutputTechnology).map(value => ({
            label: outputTechnologyTranslated[value],
            value,
          }))}
          placeholder="Select technologies"
        />
      </Form.Item>

      <FilterDivider />

      <Flex align="end" flex={2} justify="space-between">
        <Col>
          <FilterControlButtons onClickReset={handleReset} />
        </Col>

        {isMagicRole && (
          <Col>
            <Button icon={<Icon iconString="plus" />} onClick={handleCreate}>
              Create new model
            </Button>
          </Col>
        )}
      </Flex>
    </Filter>
  );
};
